import React, { useState } from 'react'
import { ORIGIN_EXTRA_PROBLEM, originExtra, originOf } from '../format'
import { LmsIcon } from '../lms-icon'

// A connection's launch settings (Phase F Task 7; rulings F-12, F-15, F-13,
// F10/F-17), drawn in the connection drawer under Security:
//   - require a sign-in for every launch (strict_sign_in; a new SCORM connection
//     starts with it on, and the create form has the same switch);
//   - the origins a browser may launch from (allowed_origins, at most 20);
//   - the anomaly block (anomaly_block, off by default);
//   - who can connect an account (link_policy: anyone | sso_only);
//   - the trust-level card: how far the connection can tell who is launching.
// The system key has its own field in the drawer's details.
//
// The LMS reads these columns (feat/lms-launch 9db3564) but its connection
// GET and PATCH do not carry them yet, so a control is drawn only when the
// connection's read carries its field, and a save sends only what changed.
// TODO(Task 9b): link_policy's server side (the LMS accepting it, and
// /lms/identity refusing a login that did not use the organization's SSO,
// LINK_SSO_REQUIRED) comes later; its select appears once the connection's
// read carries `link_policy`. Strict, allowed origins and the anomaly block
// need the same: the LMS's connection GET and PATCH must carry
// `strict_sign_in`, `allowed_origins` and `anomaly_block`.

export const MAX_ORIGINS = 20

export const LINK_POLICIES = [
  ['anyone', 'Anyone with an EducationXR account'],
  ['sso_only', "Your organization's single sign-on only (people who sign in through your organization's SSO)"]
]

const NO_SERVER_ERRORS = { list: null, entries: {} }

const sameList = (a, b) => a.length === b.length && a.every((origin, i) => origin === b[i])

// The service's word on an origin list it refused (a 400's details.fields): the
// list's own, and each entry's by its index in the list sent, keyed by entry.
const originFieldErrors = (error, sent) => {
  const fields = (error && error.details && error.details.fields) || {}
  const entries = {}
  ;(sent || []).forEach((origin, i) => {
    if (fields[`allowed_origins[${i}]`]) entries[origin] = fields[`allowed_origins[${i}]`]
  })
  return { list: fields.allowed_origins || null, entries }
}

// The settings the connection carries, as the drawer edits them. `saved` is
// what the connection had, `values` what the form holds; a field the read did
// not carry is absent from both and never drawn or sent.
export const useLaunchSettings = () => {
  const [saved, updateSaved] = useState({})
  const [values, updateValues] = useState({})
  const [draft, updateDraft] = useState('')
  const [draftError, updateDraftError] = useState(null)
  const [serverErrors, updateServerErrors] = useState(NO_SERVER_ERRORS)

  const load = connection => {
    const found = {}
    if (typeof connection.strict_sign_in === 'boolean') found.strict_sign_in = connection.strict_sign_in
    if (Array.isArray(connection.allowed_origins)) found.allowed_origins = [...connection.allowed_origins]
    if (typeof connection.anomaly_block === 'boolean') found.anomaly_block = connection.anomaly_block
    if (typeof connection.link_policy === 'string') found.link_policy = connection.link_policy
    updateSaved(found)
    updateValues(found)
  }

  const set = (field, value) => updateValues(previous => ({ ...previous, [field]: value }))

  // Only what changed: the service leaves the rest as stored.
  const changes = () => {
    const out = {}
    Object.keys(saved).forEach(field => {
      const same = field === 'allowed_origins' ? sameList(values[field], saved[field]) : values[field] === saved[field]
      if (!same) out[field] = values[field]
    })
    return out
  }

  // An origin typed but not added would be lost without a word.
  const validate = () => {
    if (!draft.trim()) return true
    updateDraftError('Press Add to allow this address, or clear it.')
    return false
  }

  const add = () => {
    const origins = values.allowed_origins || []
    const origin = originOf(draft)
    const extra = origin === null ? null : originExtra(draft)
    let problem = null
    if (origins.length >= MAX_ORIGINS) problem = `At most ${MAX_ORIGINS} origins.`
    else if (origin === null) problem = 'Enter a web address, such as https://lms.example.com.'
    else if (extra) problem = ORIGIN_EXTRA_PROBLEM[extra]
    else if (origins.includes(origin)) problem = 'Already in the list.'
    updateDraftError(problem)
    if (problem) return
    set('allowed_origins', [...origins, origin])
    updateDraft('')
  }

  const remove = origin => set('allowed_origins', (values.allowed_origins || []).filter(entry => entry !== origin))

  const clearErrors = () => {
    updateServerErrors(NO_SERVER_ERRORS)
    updateDraftError(null)
  }

  // A save the service refused: its words on the list, and on each entry.
  const refused = (error, sent) => updateServerErrors(originFieldErrors(error, sent))

  // The save bar's Discard: back to what is stored, nothing typed, no errors.
  const reset = () => {
    updateValues(saved)
    updateDraft('')
    clearErrors()
  }

  return {
    saved, values, draft, draftError, serverErrors,
    load, set, changes, validate, add, remove, clearErrors, refused, reset,
    setDraft: value => {
      updateDraft(value)
      updateDraftError(null)
    }
  }
}

// How far the connection can tell who is launching (F10, F-17, L-20). cmi5
// asks the LMS for the launch's own data with the token the LMS issued, and
// LTI 1.3 (delivery `lti13`) is the LMS's signed launch, which EdXR checks
// against the LMS's published keys: both are "Verified by the LMS". SCORM (and
// an xAPI record-store launch) can only take the learner id the LMS's own
// sign-in gave the package.
const VERIFIED = new Set(['cmi5', 'lti13'])

// An LTI 1.3 connection has no package and no SCO, so none of the SCORM
// package's launch settings (the origins a browser may launch from) apply.
const isLti = delivery => delivery === 'lti13'

// A SCORM package is opened by anyone who can open the course in the LMS, and the
// learner id it takes is the one the LMS's own sign-in gave (the package and the
// id are the page's to send), so on a connection without a sign-in for every
// launch, anyone who can open the course in the LMS can act as a learner who is
// already connected (Phase F final review H5). Strict mode asks the connected
// account's own sign-in, which prevents it. An xAPI record-store session starts
// in the app, not from the LMS.
const isScorm = delivery => !delivery || delivery === 'scorm'

// Whether a new connection of this delivery starts with a sign-in for every
// launch (owner decision 2026-10-16): SCORM does, since its package carries no
// signed learner identity; cmi5, the direct record store and LTI 1.3 (whose
// launch is signed by the platform) start with it off. The service applies the
// same default when the field is left out; the create form sends what its
// switch shows.
export const strictSignInDefault = delivery => isScorm(delivery)

export const TrustLevelCard = ({ delivery, strict }) => {
  const verified = VERIFIED.has(delivery)
  return (
    <div className="field" data-testid="trust-level">
      <div className="field-label">
        <span className="name">
          <LmsIcon name={verified ? 'circle-check' : 'shield-halved'} /> {verified ? 'Verified by the LMS' : "Trusts the LMS's own sign-in"}
        </span>
      </div>
      {verified && isLti(delivery) ? (
        <div className="field-help" style={{ marginTop: 0 }}>
          Your LMS signs every launch to say who is launching, and EducationXR checks that signature against the keys your LMS
          publishes before a course opens, so a learner cannot open a course as someone else.
        </div>
      ) : verified ? (
        <div className="field-help" style={{ marginTop: 0 }}>
          Before a course opens, EducationXR asks your LMS who is launching it, so a learner cannot open a course as someone
          else.
        </div>
      ) : (
        <div className="field-help" style={{ marginTop: 0 }}>
          This connection cannot ask the LMS who is launching: EducationXR takes the learner id the LMS's own sign-in gives
          it. Where your LMS supports it, LTI 1.3 or cmi5 checks this with the LMS itself, and is recommended. A sign-in for
          every launch adds a check of its own.
        </div>
      )}
      {!verified && isScorm(delivery) && strict === false && (
        <div className="field-help" style={{ marginTop: 6 }} data-testid="trust-level-anyone">
          <strong>Anyone who can open the course in your LMS can act as a learner who is already connected.</strong> The
          course cannot tell them apart from that learner. Turning on "Require a sign-in for every launch" prevents it: the
          launch then needs the connected account's own sign-in.
        </div>
      )}
      {!verified && isScorm(delivery) && strict === true && (
        <div className="field-help" style={{ marginTop: 6 }} data-testid="trust-level-strict">
          A sign-in for every launch is on: someone else who can open the course in your LMS cannot launch it as a learner
          who is already connected.
        </div>
      )}
    </div>
  )
}

const Toggle = ({ id, checked, onChange, label, help, helpId, disabled }) => (
  <div className="field">
    <div className="form-check form-switch">
      <input
        id={id}
        className="form-check-input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-describedby={helpId}
        onChange={e => onChange(e.target.checked)}
      />
      <label className="form-check-label" htmlFor={id}>
        {label}
      </label>
    </div>
    <div className="field-help" id={helpId} data-testid={helpId}>
      {help}
    </div>
  </div>
)

// The switch for a sign-in for every launch: the create form's and the
// connection drawer's, one control and one set of words. Its help says why it
// matters for the connection's delivery. `disabled`: the connection can no
// longer change (an archived LTI 1.3 one).
const STRICT_HELP =
  "Learners sign in to EducationXR every time they launch a course, even when their account is already connected, and the sign-in must be the connected account's."
export const StrictSignInSwitch = ({ checked, onChange, delivery, disabled }) => (
  <Toggle
    id="launch-strict"
    checked={checked}
    disabled={disabled}
    onChange={onChange}
    label="Require a sign-in for every launch"
    helpId="launch-strict-help"
    help={
      isScorm(delivery)
        ? `${STRICT_HELP} A SCORM package cannot prove who opened it, so without this anyone who can open the course in your LMS could launch it as a learner who is already connected. New SCORM connections start with it on.`
        : `${STRICT_HELP} Use it when learners share devices.`
    }
  />
)

// `readOnly`: the connection can no longer change (an archived one, or a role that may only read).
export const LaunchSettings = ({ launch, delivery, readOnly }) => {
  const { values, draft, draftError, serverErrors } = launch
  const origins = values.allowed_origins
  const full = Array.isArray(origins) && origins.length >= MAX_ORIGINS

  return (
    <div className="form-section" data-testid="launch-settings">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="lock" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Launch</h2>
      </div>

      <TrustLevelCard delivery={delivery} strict={'strict_sign_in' in values ? values.strict_sign_in : undefined} />

      {'strict_sign_in' in values && (
        <StrictSignInSwitch
          checked={values.strict_sign_in}
          disabled={readOnly}
          delivery={delivery}
          onChange={checked => launch.set('strict_sign_in', checked)}
        />
      )}

      {Array.isArray(origins) && !isLti(delivery) && (
        <div className="field" data-testid="allowed-origins">
          <div className="field-label">
            <span className="name">Allowed launch origins</span>
            <span className="optional" data-testid="allowed-origins-count">
              {origins.length} of {MAX_ORIGINS}
            </span>
          </div>
          {origins.length === 0 ? (
            <div className="field-help" style={{ marginTop: 0 }} data-testid="allowed-origins-none">
              None: no browser may start a launch from this connection's packages.
            </div>
          ) : (
            <ul className="list-unstyled mb-0">
              {origins.map(origin => (
                <li key={origin} data-testid={`allowed-origin-${origin}`} style={{ padding: '3px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="text-mono" style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {origin}
                    </span>
                    <button
                      className="btn btn-light border btn-sm"
                      type="button"
                      aria-label={`Remove ${origin}`}
                      disabled={readOnly}
                      onClick={() => launch.remove(origin)}
                    >
                      Remove
                    </button>
                  </div>
                  {serverErrors.entries[origin] && (
                    <div className="field-error">
                      <LmsIcon name="circle-exclamation" />
                      {serverErrors.entries[origin]}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              type="text"
              className={`form-control form-control-sm ${draftError ? 'is-invalid' : ''}`}
              placeholder="https://lms.acme.com"
              aria-label="Origin to allow"
              value={draft}
              disabled={full || readOnly}
              onChange={e => launch.setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  launch.add()
                }
              }}
              data-testid="allowed-origin-input"
            />
            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              disabled={full || readOnly}
              data-testid="allowed-origin-add"
              onClick={launch.add}
            >
              Add
            </button>
          </div>
          {draftError ? (
            <div className="field-error" data-testid="allowed-origin-error">
              <LmsIcon name="circle-exclamation" />
              {draftError}
            </div>
          ) : (
            <div className="field-help" data-testid="allowed-origin-help">
              {full
                ? `At most ${MAX_ORIGINS} origins. Remove one to add another.`
                : "A launch is accepted only from a browser on one of these addresses: your LMS, as its learners' browsers reach it. At most 20."}
            </div>
          )}
          {serverErrors.list && (
            <div className="field-error" data-testid="allowed-origins-server-error">
              <LmsIcon name="circle-exclamation" />
              {serverErrors.list}
            </div>
          )}
        </div>
      )}

      {'anomaly_block' in values && (
        <Toggle
          id="launch-anomaly-block"
          checked={values.anomaly_block}
          disabled={readOnly}
          onChange={checked => launch.set('anomaly_block', checked)}
          label="Block launches while something looks unusual"
          helpId="launch-anomaly-help"
          help="When one browser opens launches for several learners, or one link is opened from many browsers, staff are alerted and it shows in Activity. Off: nothing is blocked. On: launches are refused while it lasts."
        />
      )}

      {'link_policy' in values && (
        <div className="field">
          <div className="field-label">
            <span className="name">
              <label htmlFor="launch-link-policy">Who can connect an account</label>
            </span>
          </div>
          <select
            id="launch-link-policy"
            className="form-select form-select-sm"
            value={values.link_policy}
            disabled={readOnly}
            onChange={e => launch.set('link_policy', e.target.value)}
            data-testid="launch-link-policy"
          >
            {LINK_POLICIES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <div className="field-help">
            Applies to learners connecting their EducationXR account the first time they launch. Accounts already connected
            are not affected.
          </div>
        </div>
      )}
    </div>
  )
}
